import React from 'react'
import type { IntlShape } from 'react-intl'

import {
  AnalyticsIcon,
  ArrowRightIcon,
  BulletListIcon,
  ChartIcon,
  ChatIcon,
  CogIcon,
  CollectionIcon,
  FileIcon,
  InlineCodeIcon,
  MagicPenIcon,
  NoteIcon,
  PaperplaneIcon,
  PenIcon,
  PeopleIcon,
  PlugIcon,
  PlugWithSparklesIcon,
  RecycleIcon,
  RescueBeltIcon,
  SearchWithSparkleIcon,
  SliteAgentIcon,
  TemplateIcon,
  UserGroupIcon,
  VideoIcon,
} from '@slite/icons'

import { createRoutes } from '../../routes'
import { type CustomerQuoteKey, getCustomerQuote } from '../customerQuotes'

/* Pre-composed navbar card image: padding, size, and bottom clipping are
   baked into the asset. */
const ebookCompanyBrainCover =
  'https://cdn.sanity.io/images/dnsgqii5/production/5c437883ecd8131932b151ec23fd81c582fe649b-402x313.png'

type NavbarWithSubmenuId = 'Product' | 'Solutions' | 'Resources'

// Companion to a child item — when set, hovering this card in the dropdown
// swaps the right-side quote panel to this customer. Wired only for the
// Solutions menu today.
type NavbarChildItemQuote = {
  name: string
  title: string
  testimonial: string
  pictureUrl: string
  companyImageUrl?: string
}

type NavbarChildItem = {
  icon?: React.ReactNode
  title: React.ReactNode
  subtitle?: string
  url: string
  quote?: NavbarChildItemQuote
}

const ICON_SIZE = 16

// Feature columns showcase a single highlighted item with an image under it
// (e.g. the ebook in Resources). Only rendered in the desktop dropdown — the
// mobile overlay shows the items as plain links.
type NavbarFeatureImage = {
  src: string
  alt: string
}

type NavbarColumn = {
  title?: string
  items: NavbarChildItem[]
  feature?: NavbarFeatureImage
  // Untitled, but keeps the title space so one group can span two columns.
  continuesPreviousColumn?: boolean
}

export type NavbarWithSubmenu = {
  id: NavbarWithSubmenuId
  title: string
  url: string
  columns: NavbarColumn[]
  secondaryColumns?: NavbarColumn[]
}

export type NavbarWithoutSubmenu = {
  title: React.ReactNode
  url: string
  columns: []
}

function titleWithTag(title: string, tag: string) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {title}
      <span
        style={{
          background: 'var(--saturn-orange)',
          color: 'white',
          fontSize: 12,
          fontWeight: 600,
          padding: '0px 6px',
          lineHeight: '20px',
          borderRadius: 6,
        }}
      >
        {tag}
      </span>
    </span>
  )
}

// Build a NavbarChildItemQuote from a customerQuotes key. The nav panel is
// tight on space so callers usually pass a short-form `testimonial` here —
// the canonical longer quote on customerQuotes.ts stays untouched.
function navQuote(
  intl: IntlShape,
  key: CustomerQuoteKey,
  opts?: { testimonial?: string; companyImageUrl?: string }
): NavbarChildItemQuote {
  const q = getCustomerQuote(key, intl)
  return {
    name: q.name,
    title: q.title,
    testimonial: opts?.testimonial ?? q.testimonial,
    pictureUrl: q.pictureUrl,
    companyImageUrl: opts?.companyImageUrl,
  }
}

export function getNavbarLinksData(intl: IntlShape) {
  const routes = createRoutes(intl.locale)
  const NAVBAR_LINKS_DATA: (NavbarWithSubmenu | NavbarWithoutSubmenu)[] = [
    {
      id: 'Product',
      title: intl.formatMessage({
        id: 'navbar.new.product',
        defaultMessage: 'Product',
      }),
      url: '',
      columns: [
        {
          title: intl.formatMessage({
            id: 'navbar.new.product.column.knowledgeBase',
            defaultMessage: 'Capture knowledge',
          }),
          items: [
            {
              icon: <NoteIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.aiKnowledgeBase',
                defaultMessage: 'Knowledge base',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.aiKnowledgeBase.subtitle',
                defaultMessage: 'Your single source of truth',
              }),
              url: routes.solutions.knowledgeBase(),
            },
            {
              icon: <PenIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.documents',
                defaultMessage: 'Editor',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.documents.subtitle',
                defaultMessage: 'Edit docs in real time',
              }),
              url: routes.editor(),
            },
            {
              icon: <TemplateIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.templates',
                defaultMessage: 'Doc templates',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.templates.subtitle',
                defaultMessage: 'Repeatable, consistent docs',
              }),
              url: routes.templates.index(),
            },
            {
              icon: <PlugIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.integrations',
                defaultMessage: 'Integrations',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.integrations.subtitle',
                defaultMessage: '60+ sources & embeds',
              }),
              url: routes.integrations.documentIntegrations(),
            },
          ],
        },
        {
          title: intl.formatMessage({
            id: 'navbar.new.product.column.accuracy',
            defaultMessage: 'Keep it accurate',
          }),
          items: [
            {
              icon: <SliteAgentIcon size={ICON_SIZE} />,
              title: titleWithTag(
                intl.formatMessage({
                  id: 'navbar.new.ai.selfMaintaining',
                  defaultMessage: 'Doc Maintenance',
                }),
                intl.formatMessage({
                  id: 'navbar.new.tag',
                  defaultMessage: 'New',
                })
              ),
              subtitle: intl.formatMessage({
                id: 'navbar.new.ai.selfMaintaining.subtitle',
                defaultMessage: 'Drafts updates for approval',
              }),
              url: routes.blog.agentAnnouncement(),
            },
            {
              icon: <AnalyticsIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.features',
                defaultMessage: 'Knowledge Management Panel',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.features.subtitle',
                defaultMessage: 'Review health at a glance',
              }),
              url: routes.features(),
            },
            {
              icon: <RecycleIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.ai.workflows',
                defaultMessage: 'Workflow automations',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.ai.workflows.subtitle',
                defaultMessage: 'Ready-made AI workflows',
              }),
              url: routes.workflows.index(),
            },
          ],
        },
        {
          title: intl.formatMessage({
            id: 'navbar.new.product.column.ai',
            defaultMessage: 'Find answers',
          }),
          items: [
            {
              icon: <SearchWithSparkleIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.ai.search',
                defaultMessage: 'AI search',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.ai.search.subtitle',
                defaultMessage: 'Find anything in your stack',
              }),
              url: routes.aiSearch(),
            },
            {
              icon: <BulletListIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.ai.bulkMode',
                defaultMessage: 'Bulk mode',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.ai.bulkMode.subtitle',
                defaultMessage: 'RFPs and questionnaires',
              }),
              url: routes.askInBulk(),
            },
            {
              icon: <PaperplaneIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.ai.digests',
                defaultMessage: 'Digests',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.ai.digests.subtitle',
                defaultMessage: 'AI reports on autopilot',
              }),
              url: routes.digests(),
            },
            {
              icon: <PlugWithSparklesIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.ai.agentSources',
                defaultMessage: 'Agent Sources',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.ai.agentSources.subtitle',
                defaultMessage: 'Sources your agent can read',
              }),
              url: routes.integrations.sources(),
            },
          ],
        },
      ],
      secondaryColumns: [
        {
          title: intl.formatMessage({
            id: 'navbar.new.product.column.getStarted',
            defaultMessage: 'Get started',
          }),
          items: [
            {
              title: intl.formatMessage({
                id: 'navbar.new.getStarted.desktopApp',
                defaultMessage: 'Download App',
              }),
              url: routes.download(),
            },
            {
              title: intl.formatMessage({
                id: 'navbar.new.getStarted.browserExtension',
                defaultMessage: 'Browser Extension',
              }),
              url: routes.browserExtension(),
            },
            {
              title: intl.formatMessage({
                id: 'navbar.new.getStarted.slackApp',
                defaultMessage: 'Slack App',
              }),
              url: routes.external.helpCenter.url,
            },
            {
              title: intl.formatMessage({
                id: 'navbar.new.apps.mcp',
                defaultMessage: 'MCP',
              }),
              url: routes.mcp(),
            },
          ],
        },
      ],
    },
    {
      id: 'Solutions',
      title: intl.formatMessage({
        id: 'navbar.new.solutions',
        defaultMessage: 'Solutions',
      }),
      url: '',
      columns: [
        {
          title: intl.formatMessage({
            id: 'navbar.new.solution.column.useCase',
            defaultMessage: 'Use cases',
          }),
          items: [
            {
              icon: <RecycleIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.selfMaintainingDoc',
                defaultMessage: 'Self-maintaining docs',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.solutions.selfMaintainingDoc.subtitle',
                defaultMessage: 'Your single source of truth on auto-pilot',
              }),
              url: routes.solutions.detail({
                slug: 'self-maintaining-documentation',
              }),
            },
            {
              icon: <FileIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.scaffoldWithAgent',
                defaultMessage: 'Automated documentation',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.solutions.scaffoldWithAgent.subtitle',
                defaultMessage: 'Create structured docs from scratch',
              }),
              url: routes.solutions.detail({
                slug: 'automated-documentation',
              }),
            },
            {
              // "See all" — description-less, with a trailing arrow rendered
              // inline in the title (NavMenuCard puts `icon` on the left).
              icon: <ArrowRightIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.seeAll',
                defaultMessage: 'See all',
              }),
              url: routes.solutions.index(),
            },
          ],
        },
        {
          title: intl.formatMessage({
            id: 'navbar.new.solution.column.teams',
            defaultMessage: 'Teams',
          }),
          items: [
            {
              icon: <CogIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.forItOperations',
                defaultMessage: 'IT & Operations',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.solutions.forItOperations.subtitle',
                defaultMessage: 'Boost operational efficiency',
              }),
              url: routes.solutions.itOperations(),
              // Scaffold mapping — swap CustomerQuoteKey + add companyImageUrl
              // once we have category-perfect quotes/logos picked.
              quote: navQuote(intl, 'DanFlexDealer', {
                testimonial: intl.formatMessage({
                  id: 'navbar.solutions.quote.itOperations',
                  defaultMessage:
                    "The best Knowledge Base experience we've had: simple, useful, well-structured.",
                }),
              }),
            },
            {
              icon: <InlineCodeIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.forProductEngineering',
                defaultMessage: 'Product & Engineering',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.solutions.forProductEngineering.subtitle',
                defaultMessage: 'Automate documentation',
              }),
              url: routes.solutions.productEngineering(),
              quote: navQuote(intl, 'AlexisDupontAgorapulseMCP', {
                testimonial: intl.formatMessage({
                  id: 'navbar.solutions.quote.productEngineering',
                  defaultMessage:
                    'We use Slite MCP on Claude for docs, and AI search to find them across Slack and Drive.',
                }),
              }),
            },
            {
              icon: <PeopleIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.forHr',
                defaultMessage: 'Human Resources',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.solutions.forHr.subtitle',
                defaultMessage: 'Verified docs and answers',
              }),
              url: routes.solutions.humanResources(),
              quote: navQuote(intl, 'JosselinMeeroGrowth', {
                testimonial: intl.formatMessage({
                  id: 'navbar.solutions.quote.humanResources',
                  defaultMessage:
                    'We grew from 250 to 700 people in a year. Slite gave us one place to route every question.',
                }),
              }),
            },
            {
              icon: <ChatIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.forCustomerSupport',
                defaultMessage: 'Customer Support',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.solutions.forCustomerSupport.subtitle',
                defaultMessage: 'Resolve tickets 2x faster',
              }),
              url: routes.solutions.customerSupport(),
              quote: navQuote(intl, 'OskarRemoteSenseiSupport', {
                testimonial: intl.formatMessage({
                  id: 'navbar.solutions.quote.customerSupport',
                  defaultMessage:
                    'Swift response with the fix exactly as requested, every time.',
                }),
              }),
            },
            {
              icon: <ChartIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.solutions.forSales',
                defaultMessage: 'Sales',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.solutions.forSales.subtitle',
                defaultMessage: 'Playbooks that stay up to date',
              }),
              url: routes.solutions.sales(),
              quote: navQuote(intl, 'ClaireVilletDisplayceAsk', {
                testimonial: intl.formatMessage({
                  id: 'navbar.solutions.quote.sales',
                  defaultMessage:
                    "We've eliminated the endless 'How does X work' and 'Where can I find Y' messages.",
                }),
              }),
            },
          ],
        },
      ],
      // Secondary "Customers" column intentionally removed — the right-side
      // quote panel replaces it now.
    },
    {
      id: 'Resources',
      title: intl.formatMessage({
        id: 'navbar.new.resources',
        defaultMessage: 'Resources',
      }),
      url: '',
      columns: [
        {
          title: intl.formatMessage({
            id: 'navbar.new.resources.column.ebook',
            defaultMessage: 'Ebook',
          }),
          feature: {
            src: ebookCompanyBrainCover,
            alt: intl.formatMessage({
              id: 'navbar.new.ebookCompanyBrain.coverAlt',
              defaultMessage: 'Company Brain ebook cover',
            }),
          },
          items: [
            {
              title: intl.formatMessage({
                id: 'navbar.new.ebookCompanyBrain',
                defaultMessage: 'Company Brain',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.ebookCompanyBrain.subtitle',
                defaultMessage: 'The State of Company Brain',
              }),
              url: routes.ebooks.companyBrain(),
            },
          ],
        },
        {
          title: intl.formatMessage({
            id: 'navbar.new.resources.column.resources',
            defaultMessage: 'Resources',
          }),
          items: [
            {
              icon: <MagicPenIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.learn',
                defaultMessage: 'Learn',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.learn.subtitle',
                defaultMessage: 'Master knowledge management',
              }),
              url: routes.learn.index(),
            },
            {
              icon: <FileIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.blog',
                defaultMessage: 'Blog',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.blog.subtitle',
                defaultMessage: 'Future of team knowledge',
              }),
              url: routes.blog.index(),
            },
            {
              icon: <VideoIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.webinars',
                defaultMessage: 'Webinars',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.webinars.subtitle',
                defaultMessage: 'Talks and recaps to watch',
              }),
              url: routes.webinars.index(),
            },
            {
              icon: <UserGroupIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.customers',
                defaultMessage: 'Customers',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.customers.subtitle',
                defaultMessage: 'Stories from teams like yours',
              }),
              url: routes.customers.index(),
            },
          ],
        },
        {
          continuesPreviousColumn: true,
          items: [
            {
              icon: <RescueBeltIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.helpCenter',
                defaultMessage: 'Help center',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.helpCenter.subtitle',
                defaultMessage: 'Advice and answers',
              }),
              url: routes.helpCenter.index(),
            },
            {
              icon: <CollectionIcon size={ICON_SIZE} />,
              title: intl.formatMessage({
                id: 'navbar.new.resourcesHub',
                defaultMessage: 'Resources hub',
              }),
              subtitle: intl.formatMessage({
                id: 'navbar.new.resourcesHub.subtitle',
                defaultMessage: 'Guides, tools, and more',
              }),
              url: routes.resources(),
            },
          ],
        },
      ],
      secondaryColumns: [
        {
          title: intl.formatMessage({
            id: 'navbar.new.resources.column.company',
            defaultMessage: 'Company',
          }),
          items: [
            {
              title: intl.formatMessage({
                id: 'navbar.new.company.about',
                defaultMessage: 'About',
              }),
              url: routes.about(),
            },
            {
              title: intl.formatMessage({
                id: 'navbar.new.company.career',
                defaultMessage: 'Career',
              }),
              url: routes.jobs(),
            },
            {
              title: intl.formatMessage({
                id: 'navbar.new.company.changelog',
                defaultMessage: 'Changelog',
              }),
              url: routes.changelog.index(),
            },
            {
              title: intl.formatMessage({
                id: 'navbar.new.company.contactSupport',
                defaultMessage: 'Contact support',
              }),
              url: 'mailto:support@slite.com',
            },
          ],
        },
      ],
    },
    {
      title: titleWithTag(
        intl.formatMessage({
          id: 'navbar.new.agent',
          defaultMessage: 'Slite Agent',
        }),
        intl.formatMessage({
          id: 'navbar.new.tag',
          defaultMessage: 'New',
        })
      ),
      url: routes.agent(),
      columns: [],
    },
    {
      title: intl.formatMessage({
        id: 'navbar.new.pricing',
        defaultMessage: 'Pricing',
      }),
      url: routes.pricing(),
      columns: [],
    },
  ]

  return NAVBAR_LINKS_DATA
}
